import { SprintProductEntity, api } from '@data';
import {
  ApprovalRecipients,
  ApproveSprintFormValues,
  DisputeSprintFormValues,
  ListResponse,
  MembershipEntity,
  SendSprintFormValues,
  SprintProductStatus,
  SprintProductClientEntity,
  SprintReportMailDraft,
  MailBlock,
  ClientProductEntity,
  UnclosedSprintEntity,
} from '@data/utils/types';

export const createSprintProduct = (
  organizationId: string,
  values: Partial<SprintProductEntity>,
): Promise<SprintProductEntity> => api.post('/sprintproducts', values);

export const updateSprintProduct = (values: Partial<SprintProductEntity>): Promise<SprintProductEntity> =>
  api.patch(`/sprintproducts/${values.id}`, values);

export const sendSprintProduct = (values: SendSprintFormValues): Promise<SprintProductEntity> =>
  api.patch(`/sprintproducts/${values.sprintProductId}/send`, values);

/** The mail sending would put out, composed by the API so the preview and the send cannot drift. */
export const fetchSprintReportMailDraft = (
  sprintProductId: string,
  body: { firstName?: string; totalCost?: number; blocks?: MailBlock[] },
): Promise<SprintReportMailDraft> => api.post(`/sprintproducts/${sprintProductId}/mail-draft`, body);

/** Who the product's last report before this one went to, to start this one addressed the same way. */
export const fetchPreviousRecipients = (sprintProductId: string): Promise<ListResponse<MembershipEntity>> =>
  api.get(`/sprintproducts/${sprintProductId}/previous-recipients`);

export const approveSprintProduct = (values?: ApproveSprintFormValues): Promise<ClientProductEntity> =>
  api.patch(`/sprintproducts/${values?.sprintProductId}/approve`, values);

export const disputeSprintProduct = (values?: DisputeSprintFormValues): Promise<ClientProductEntity> =>
  api.patch(`/sprintproducts/${values?.sprintProductId}/dispute`, values);

export const fetchClientSprintProducts = (
  organizationId?: string,
  params?: {
    statuses?: Partial<SprintProductStatus>[];
    // The preview: one of us reading this product's list as the client reads it. Both are sent together
    // or neither, since there is no client to scope by when it is one of us asking.
    productId?: string;
    asClient?: boolean;
  },
): Promise<ListResponse<SprintProductClientEntity>> =>
  // This is a client-scoped endpoint: it only returns sprint reports the caller is a recipient of.
  // Internal (PM/admin) screens want fetchPmSprintProducts instead. Non-client members reading this
  // get a 403 (NoAccessToOrganization); treat that as an expected, silent outcome (the caller falls
  // back to an empty list) rather than surfacing a global error toast.
  api.get(`/organizations/${organizationId}/client/sprintproducts`, {
    params,
    silentStatuses: [403],
  });

// The PM/admin view of the same data: every sprint report in the org with the given statuses,
// regardless of who the client recipients are. Use this (not the client endpoint) for internal
// screens like Insights — the client one only ever returns reports addressed to the caller.
export const fetchPmSprintProducts = (
  organizationId?: string,
  params?: {
    statuses?: Partial<SprintProductStatus>[];
    /** only reports actually mailed to a client */
    sent?: boolean;
    /** only reports whose sprint has time logged on the product */
    hasReportedTime?: boolean;
    /** past the backlog cutoff as well, for a caller reading one year of one product */
    wholeHistory?: boolean;
    /** One product's rows, bound in the query rather than filtered after the fact. */
    productId?: string;
  },
  // The org's own view, so the report shape rather than a recipient's: the route answers with
  // SprintProductRepresentation. The prototype typed this as the client entity, which it never was.
): Promise<ListResponse<SprintProductEntity>> =>
  api.get(`/organizations/${organizationId}/pm/sprintproducts`, { params });

// The step before fetchPmSprintProducts in the reporting chain: sprints that have ended with time on
// them and no closed report. Keyed by sprint + product rather than by report id, because the sprints
// most in need of a close are the ones nobody has opened a report on yet.
export const fetchPmUnclosedSprints = (
  organizationId?: string,
  params?: { offset?: number; limit?: number },
): Promise<ListResponse<UnclosedSprintEntity>> =>
  api.get(`/organizations/${organizationId}/pm/sprintproducts/unclosed`, { params });

/** "We looked, and the report stands", with the reason when one was given. */
export const dismissDispute = (sprintProductId?: string, note?: string): Promise<SprintProductEntity> =>
  api.post(`/sprintproducts/${sprintProductId}/dispute/dismiss`, { note });

export const markApprovalCommentRead = (sprintProductId: string): Promise<SprintProductEntity> =>
  api.patch(`/sprintproducts/${sprintProductId}/comment-read`);

/** How many reports carry a comment the caller has not read, across the products they answer for. */
export const fetchUnreadCommentCount = (organizationId?: string): Promise<{ count: number; sprintIds: string[] }> =>
  api.get(`/organizations/${organizationId}/pm/unread-comments`);

/** Reads the caller's whole queue in one go, answering with exactly what it marked so it can be undone. */
export const markAllCommentsRead = (organizationId?: string): Promise<{ sprintProductIds: string[] }> =>
  api.post(`/organizations/${organizationId}/pm/unread-comments/read`, {});

export const undoMarkAllCommentsRead = (
  organizationId: string | undefined,
  sprintProductIds: string[],
): Promise<void> => api.post(`/organizations/${organizationId}/pm/unread-comments/unread`, { sprintProductIds });

export const fetchApprovalRecipients = (sprintProductId?: string): Promise<ApprovalRecipients> =>
  api.get(`/sprintproducts/${sprintProductId}/approval-recipients`);

export const fetchSprintProductClients = (sprintProductId?: string): Promise<ListResponse<MembershipEntity>> =>
  api.get(`/sprintproducts/${sprintProductId}/clients`);

export const fetchSprintProductClientUrl = (sprintProductId?: string, clientId?: string): Promise<{ url: string }> =>
  api.get(`/sprintproducts/${sprintProductId}/clients/${clientId}/url`);

export const removeSprintProductClient = (sprintProductId?: string, clientId?: string): Promise<void> =>
  api.delete(`/sprintproducts/${sprintProductId}/clients/${clientId}`);

export const resendSprintProduct = (sprintId?: string, productId?: string): Promise<void> =>
  api.patch(`/sprints/${sprintId}/products/${productId}/resend`);
